import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Download, CheckCircle2 } from "lucide-react";

export default function AdminWaitlist() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | notified | converted | new

  useEffect(() => { api.get("/admin/waitlist").then((r) => setRows(r.data)); }, []);

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "notified") return !!r.notified_at;
    if (filter === "converted") return !!r.converted_at;
    if (filter === "new") return !r.notified_at && !r.converted_at;
    return true;
  });

  const exportCsv = () => {
    const header = "email,source,joined_at,notified_at,converted_at";
    const body = rows.map((r) => [r.email, r.source, r.joined_at, r.notified_at || "", r.converted_at || ""].join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const total = rows.length;
  const converted = rows.filter((r) => r.converted_at).length;

  return (
    <div className="space-y-14" data-testid="admin-waitlist">
      <header>
        <div className="label-eyebrow mb-3">Admin · Waitlist</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">Every soul on the <em className="italic text-[hsl(var(--primary))]">list</em>.</h1>
        <p className="mt-6 text-stone-600 max-w-xl leading-relaxed">Pre-launch signups. They lock in $9.99 per month for life when they subscribe.</p>
      </header>

      <section className="grid md:grid-cols-4 gap-0 border-t border-b border-border">
        <Stat label="Total signups" value={total} />
        <Stat label="Converted" value={converted} />
        <Stat label="Pending notify" value={rows.filter((r) => !r.notified_at).length} />
        <Stat label="Conversion rate" value={total ? `${Math.round((converted / total) * 100)}%` : "—"} />
      </section>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-wrap gap-2" data-testid="waitlist-filter">
          {[
            ["all", "All"], ["new", "New"], ["notified", "Notified"], ["converted", "Converted"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-4 py-1.5 text-xs uppercase tracking-widest border transition-colors ${filter === k ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-border hover:border-stone-500"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} data-testid="btn-export-csv" className="inline-flex items-center gap-2 text-sm border border-border px-4 py-2 hover:bg-[hsl(var(--foreground))] hover:text-white transition-colors">
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="text-stone-500 italic font-serif text-xl">No signups match this view yet.</p>
      ) : (
        <table className="w-full text-sm border-t border-border" data-testid="waitlist-table">
          <thead>
            <tr className="text-left text-stone-500">
              <th className="py-3 label-eyebrow">Email</th>
              <th className="py-3 label-eyebrow">Source</th>
              <th className="py-3 label-eyebrow">Joined</th>
              <th className="py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.email} className="border-t border-border" data-testid={`row-${r.email}`}>
                <td className="py-3 font-serif"><Mail className="inline w-3.5 h-3.5 mr-2 text-stone-400" strokeWidth={1.5} />{r.email}</td>
                <td className="py-3 text-stone-500 italic">{r.source}</td>
                <td className="py-3 text-stone-500">{new Date(r.joined_at).toLocaleDateString()}</td>
                <td className="py-3">
                  {r.converted_at ? (
                    <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--secondary))]"><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Converted</span>
                  ) : r.notified_at ? (
                    <span className="text-xs text-stone-500">Notified</span>
                  ) : (
                    <span className="text-xs text-[hsl(var(--primary))]">Pending</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-6 md:p-8 border-r last:border-r-0 border-border">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="font-serif text-4xl">{value}</div>
    </div>
  );
}
